import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LoginPage.css';
import { useAuth } from './useAuth';
import { trackSignInClick, trackLoginFailed, trackTermsOpened, trackPolicyOpened, trackRefundPolicyOpened } from './userJourneyTracker';

const LoginPage = ({ isOpen, onClose, mode = 'signup', onModeChange }) => {
  const { signInWithGoogle, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoogleSignIn = async () => {
    trackSignInClick();
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        onClose(); // Close modal on successful sign in
      } else {
        trackLoginFailed(result.error || 'Authentication failed');
        setError(result.error || 'Authentication failed. Please try again.');
      }
    } catch (err) {
      trackLoginFailed(err?.message || 'An error occurred');
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const isSignUp = mode === 'signup';
  const isSignIn = mode === 'signin';

  return (
    <div className="login-modal-overlay" onClick={handleClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="login-modal-close" onClick={handleClose}>
          ×
        </button>
        
        <div className="login-modal-header">
          <h2 className="login-modal-title">
            {isSignUp ? 'Create an account' : 'Sign in to your account'}
          </h2>
          <p className="login-modal-subtitle">
            {isSignUp 
              ? 'Get started with your video processing journey' 
              : 'Welcome back! Sign in to continue'}
          </p>
        </div>
        
        {error && (
          <div className="login-error-message">
            {error}
          </div>
        )}
        
        {/* Google Sign In Button */}
        <div className="google-signup-container">
          <button 
            type="button"
            className="google-signup-btn"
            onClick={handleGoogleSignIn}
            disabled={isLoading || authLoading}
          >
            <div className="google-logo">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="white"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white"/>
              </svg>
            </div>
            {isLoading || authLoading 
              ? (isSignUp ? 'Creating Account...' : 'Signing in...')
              : (isSignUp ? 'Sign up with Google' : 'Sign in with Google')
            }
          </button>
        </div>
        
        <div className="login-modal-footer">
            {isSignUp ? (
            <p className="login-footer-text">
              Already have an account?{' '}
              <span 
                className="login-link" 
                onClick={(e) => {
                  e.stopPropagation();
                  if (onModeChange) {
                    onModeChange('signin');
                  }
                }}
              >
                Sign in
              </span>
            </p>
          ) : (
            <p className="login-footer-text">
              Don't have an account?{' '}
              <span 
                className="login-link"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onModeChange) {
                    onModeChange('signup');
                  }
                }}
              >
                Sign up
              </span>
            </p>
          )}
          <p className="login-terms">
            By continuing, I agree to the{' '}
            <Link
              to="/terms"
              className="login-link"
              onClick={(e) => {
                e.stopPropagation();
                trackTermsOpened();
                handleClose();
              }}
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/policy"
              className="login-link"
              onClick={(e) => {
                e.stopPropagation();
                trackPolicyOpened();
                handleClose();
              }}
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              to="/refund"
              className="login-link"
              onClick={(e) => {
                e.stopPropagation();
                trackRefundPolicyOpened();
                handleClose();
              }}
            >
              Refund Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
